import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Mail,
  Clock,
  Send,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Bell,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API_BASE_URL } from "../../constants";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const SCHEDULE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DELIVERY_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "slack", label: "Slack" },
  { value: "webhook", label: "Webhook" },
];

const DELIVERY_ICONS = {
  email: Mail,
  slack: Zap,
  webhook: Send,
};

const SCHEDULE_COLORS = {
  daily: "bg-blue-100 text-blue-700",
  weekly: "bg-amber-100 text-amber-700",
  monthly: "bg-purple-100 text-purple-700",
};

const EMPTY_FORM = {
  briefing_name: "",
  schedule: "daily",
  delivery_method: "email",
  delivery_destination: "",
  query_topics: "",
  summary_prompt: "",
  is_active: true,
};

const ScheduledBriefings = () => {
  const { token } = useAuth();

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const flash = (type, message) => {
    setStatusMsg({ type, message });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  // Fetch facilities
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setFacilities(list);
          if (list.length > 0 && !facilityId) {
            setFacilityId(list[0].facility_id || "");
          }
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
    };
    fetchFacilities();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch briefings
  const fetchBriefings = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/get-scheduled-briefing?facility_id=${encodeURIComponent(facilityId)}`,
        { headers },
      );
      if (res.ok) {
        const data = await res.json();
        setBriefings(Array.isArray(data) ? data : []);
      } else {
        setBriefings([]);
        flash("error", "Failed to load briefings");
      }
    } catch (err) {
      console.error("Failed to load briefings:", err);
      setBriefings([]);
      flash("error", "Failed to load briefings");
    }
    setLoading(false);
  }, [token, facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchBriefings();
  }, [fetchBriefings]);

  // Toggle active
  const handleToggleActive = async (briefing) => {
    try {
      const res = await fetch(`${API_BASE_URL}/set-scheduled-briefing`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          id: briefing.id,
          facility_id: facilityId,
          briefing_name: briefing.briefing_name,
          schedule: briefing.schedule,
          delivery_method: briefing.delivery_method,
          delivery_destination: briefing.delivery_destination,
          query_topics: briefing.query_topics,
          summary_prompt: briefing.summary_prompt,
          is_active: !briefing.is_active,
        }),
      });
      if (res.ok) {
        flash(
          "success",
          `Briefing ${!briefing.is_active ? "activated" : "paused"}`,
        );
        fetchBriefings();
      } else {
        flash("error", "Failed to update briefing");
      }
    } catch (err) {
      console.error("Toggle failed:", err);
      flash("error", "Failed to update briefing");
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (briefing) => {
    setEditingId(briefing.id);
    setFormData({
      briefing_name: briefing.briefing_name || "",
      schedule: briefing.schedule || "daily",
      delivery_method: briefing.delivery_method || "email",
      delivery_destination: briefing.delivery_destination || "",
      query_topics: Array.isArray(briefing.query_topics)
        ? briefing.query_topics.join(", ")
        : briefing.query_topics || "",
      summary_prompt: briefing.summary_prompt || "",
      is_active: briefing.is_active ?? true,
    });
    setShowModal(true);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!formData.briefing_name.trim()) {
      flash("error", "Briefing name is required");
      return;
    }
    if (!formData.delivery_destination.trim()) {
      flash("error", "Destination is required");
      return;
    }

    setSaving(true);
    try {
      let topics = formData.query_topics;
      // Try parsing as JSON array; fall back to comma-separated
      try {
        const parsed = JSON.parse(topics);
        if (Array.isArray(parsed)) topics = parsed;
      } catch {
        topics = topics
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      }

      const payload = {
        facility_id: facilityId,
        briefing_name: formData.briefing_name.trim(),
        schedule: formData.schedule,
        delivery_method: formData.delivery_method,
        delivery_destination: formData.delivery_destination.trim(),
        query_topics: topics,
        summary_prompt: formData.summary_prompt.trim(),
        is_active: formData.is_active,
      };
      if (editingId) payload.id = editingId;

      const res = await fetch(`${API_BASE_URL}/set-scheduled-briefing`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        flash("success", editingId ? "Briefing updated" : "Briefing created");
        setShowModal(false);
        fetchBriefings();
      } else {
        const err = await res.json().catch(() => ({}));
        flash("error", err.error || "Failed to save briefing");
      }
    } catch (err) {
      console.error("Save failed:", err);
      flash("error", "Failed to save briefing");
    }
    setSaving(false);
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE_URL}/delete-scheduled-briefing`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: deleteTarget.id }),
      });
      if (res.ok) {
        flash("success", "Briefing deleted");
        setDeleteTarget(null);
        fetchBriefings();
      } else {
        flash("error", "Failed to delete briefing");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      flash("error", "Failed to delete briefing");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatTopics = (topics) => {
    if (!topics) return "--";
    if (Array.isArray(topics)) return topics.join(", ");
    return String(topics);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Bell className="w-6 h-6 text-slate-700" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Scheduled Briefings
            </h2>
            <p className="text-sm text-slate-500">
              Configure automated knowledge base digest delivery
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchBriefings}
            disabled={loading}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={handleCreate}
            disabled={!facilityId}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Briefing</span>
          </button>
        </div>
      </div>

      {/* Status message */}
      {statusMsg && (
        <div
          className={`px-4 py-3 rounded-lg text-sm font-medium ${
            statusMsg.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {statusMsg.message}
        </div>
      )}

      {/* Facility Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Facility
        </label>
        <div className="relative">
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 pr-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
          >
            <option value="">Select a facility...</option>
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.display_name || f.facility_id}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {/* Briefings Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            <span className="ml-3 text-slate-500 text-sm">
              Loading briefings...
            </span>
          </div>
        ) : !facilityId ? (
          <div className="text-center py-16">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              Select a facility to view briefings
            </p>
          </div>
        ) : briefings.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              No briefings configured — create one to receive automated KB
              digests
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Schedule
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Delivery
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Destination
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Topics
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Last Sent
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">
                    Active
                  </th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {briefings.map((b) => {
                  const DeliveryIcon =
                    DELIVERY_ICONS[b.delivery_method] || Send;
                  return (
                    <tr
                      key={b.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="font-medium text-slate-800">
                            {b.briefing_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            SCHEDULE_COLORS[b.schedule] ||
                            "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          {b.schedule}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1.5 text-slate-600">
                          <DeliveryIcon className="w-3.5 h-3.5" />
                          <span className="capitalize">
                            {b.delivery_method}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-slate-600 max-w-xs truncate block"
                          title={b.delivery_destination}
                        >
                          {b.delivery_destination}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="text-slate-500 max-w-xs truncate block text-xs"
                          title={formatTopics(b.query_topics)}
                        >
                          {formatTopics(b.query_topics)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {formatDate(b.last_sent_at)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleActive(b)}
                          className="inline-flex items-center transition-colors"
                          title={
                            b.is_active
                              ? "Click to deactivate"
                              : "Click to activate"
                          }
                        >
                          {b.is_active ? (
                            <ToggleRight className="w-6 h-6 text-green-500" />
                          ) : (
                            <ToggleLeft className="w-6 h-6 text-slate-300" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleEdit(b)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(b)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingId ? "Edit Briefing" : "New Briefing"}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {editingId
                  ? "Update briefing configuration"
                  : "Configure a new automated KB digest"}
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Briefing Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Briefing Name
                </label>
                <input
                  type="text"
                  value={formData.briefing_name}
                  onChange={(e) =>
                    setFormData({ ...formData, briefing_name: e.target.value })
                  }
                  placeholder="e.g. Weekly Policy Summary"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                />
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Schedule
                </label>
                <select
                  value={formData.schedule}
                  onChange={(e) =>
                    setFormData({ ...formData, schedule: e.target.value })
                  }
                  className="w-full appearance-none border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                >
                  {SCHEDULE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Delivery Method */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Delivery Method
                </label>
                <select
                  value={formData.delivery_method}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      delivery_method: e.target.value,
                    })
                  }
                  className="w-full appearance-none border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                >
                  {DELIVERY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Destination */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Destination
                </label>
                <input
                  type="text"
                  value={formData.delivery_destination}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      delivery_destination: e.target.value,
                    })
                  }
                  placeholder={
                    formData.delivery_method === "email"
                      ? "user@example.com"
                      : formData.delivery_method === "slack"
                        ? "#channel or webhook URL"
                        : "https://webhook.example.com/endpoint"
                  }
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {formData.delivery_method === "email"
                    ? "Email address for digest delivery"
                    : formData.delivery_method === "slack"
                      ? "Slack channel or incoming webhook URL"
                      : "HTTP endpoint to receive POST payloads"}
                </p>
              </div>

              {/* Query Topics */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Query Topics
                </label>
                <textarea
                  value={formData.query_topics}
                  onChange={(e) =>
                    setFormData({ ...formData, query_topics: e.target.value })
                  }
                  placeholder='Comma-separated topics or JSON array, e.g. "policy updates, budget, compliance"'
                  rows={3}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 resize-none"
                />
              </div>

              {/* Summary Prompt */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Summary Prompt{" "}
                  <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={formData.summary_prompt}
                  onChange={(e) =>
                    setFormData({ ...formData, summary_prompt: e.target.value })
                  }
                  placeholder="Custom instructions for how the digest should be summarized..."
                  rows={3}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 resize-none"
                />
              </div>

              {/* Active */}
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, is_active: !formData.is_active })
                  }
                  className="flex items-center"
                >
                  {formData.is_active ? (
                    <ToggleRight className="w-6 h-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-6 h-6 text-slate-300" />
                  )}
                </button>
                <span className="text-sm text-slate-700">
                  {formData.is_active
                    ? "Active — briefing will run on schedule"
                    : "Inactive — briefing is paused"}
                </span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-5">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">
                  Delete Briefing
                </h3>
              </div>
              <p className="text-sm text-slate-600">
                Are you sure you want to delete{" "}
                <span className="font-medium text-slate-800">
                  {deleteTarget.briefing_name}
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledBriefings;
